import { useState } from "react";
import {
  SUPPORT_ESCALATION_CATEGORIES,
  SUPPORT_ESCALATION_CATEGORY_LABELS,
  SUPPORT_ESCALATION_NOTE_MAX,
  type HandoffToRole,
  type SupportEscalationCategory,
} from "@care-by-ella/contracts";
import {
  standardReplyOffered,
  supportEscalationOutcome,
} from "@/lib/support-escalation";
import { escalateSupportThread } from "@/lib/support-inbox-api";

// TES-1110 (Emelie 2026-10-05, Ö-331) — "Eskalera till vårdteamet" in the
// Support inbox: hand a medical question to the doctor or the coach.
//
// The handoff carries a category and an optional short note. The patient's
// message is linked, not copied, so the note asks for context rather than a
// transcript. The standard reply (Ö-333, TES-1119) is offered only when the
// server has the text (`redirectReply` non-null) and only on an escalation
// to the doctor.

const ROLE_LABEL: Record<HandoffToRole, string> = {
  doctor: "Läkaren",
  coach: "Coachen",
};

export function SupportEscalateForm({
  patientId,
  messageId,
  redirectReply,
  onDone,
  onCancel,
  onError,
}: {
  patientId: string;
  /** The patient message being escalated; absent = the whole thread. */
  messageId?: string;
  /** The approved redirect reply, or null while there is none. */
  redirectReply: string | null;
  onDone: (outcome: string) => void;
  onCancel: () => void;
  onError: (e: unknown) => void;
}) {
  const [toRole, setToRole] = useState<HandoffToRole>("doctor");
  const [category, setCategory] = useState<SupportEscalationCategory | "">("");
  const [note, setNote] = useState("");
  const [sendReply, setSendReply] = useState(false);
  const [busy, setBusy] = useState(false);

  // TES-1119 — the standard reply promises a medical notice, which only an
  // escalation to the doctor leads to; the server refuses it otherwise.
  const replyOffered = standardReplyOffered(toRole, redirectReply);
  const noteRequired = category === "other";
  const ready = category !== "" && (!noteRequired || note.trim().length > 0);

  async function submit() {
    if (busy || category === "" || !ready) return;
    setBusy(true);
    try {
      const res = await escalateSupportThread(patientId, {
        toRole,
        category,
        note: note.trim() || undefined,
        messageId,
        sendRedirectReply: replyOffered && sendReply,
      });
      onDone(supportEscalationOutcome(toRole, res));
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      aria-label="Eskalera till vårdteamet"
      className="flex flex-col gap-sm rounded-md border border-warning/40 bg-warning/5 p-sm text-sm"
    >
      <p className="font-medium">
        Eskalera {messageId ? "meddelandet" : "tråden"} till vårdteamet
      </p>
      <p className="text-xs text-textSecondary">
        Vårdteamet får ett ärende i sin kö med en länk till{" "}
        {messageId ? "meddelandet" : "tråden"}. Patientens text kopieras inte.
      </p>

      <fieldset className="flex flex-wrap gap-md">
        <legend className="sr-only">Till</legend>
        {(["doctor", "coach"] as const).map((r) => (
          <label key={r} className="flex items-center gap-xs">
            <input
              type="radio"
              name="support-escalation-to"
              checked={toRole === r}
              onChange={() => setToRole(r)}
            />
            {ROLE_LABEL[r]}
          </label>
        ))}
      </fieldset>

      <label className="flex flex-col gap-xs">
        <span>Kategori</span>
        <select
          value={category}
          onChange={(e) =>
            setCategory(e.target.value as SupportEscalationCategory | "")
          }
          className="rounded-md border border-surfaceMuted bg-background p-xs"
        >
          <option value="">Välj kategori</option>
          {SUPPORT_ESCALATION_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {SUPPORT_ESCALATION_CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-xs">
        <span>
          Anteckning {noteRequired ? "(krävs för Annat)" : "(valfri)"}
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          maxLength={SUPPORT_ESCALATION_NOTE_MAX}
          placeholder="Kort sammanhang för vårdteamet"
          className="rounded-md border border-surfaceMuted bg-background p-xs"
        />
      </label>

      {replyOffered ? (
        <label className="flex items-start gap-xs">
          <input
            type="checkbox"
            checked={sendReply}
            onChange={(e) => setSendReply(e.target.checked)}
            className="mt-[3px]"
          />
          <span>
            Skicka standardsvaret till patienten som Ella:
            <span className="mt-xs block whitespace-pre-wrap text-textSecondary">
              {redirectReply}
            </span>
          </span>
        </label>
      ) : null}

      <div className="flex justify-end gap-sm">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-surfaceMuted px-md py-xs text-textSecondary hover:text-textPrimary"
        >
          Avbryt
        </button>
        <button
          type="submit"
          disabled={busy || !ready}
          className="rounded-md bg-brand px-md py-xs text-white hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Eskalerar…" : "Eskalera"}
        </button>
      </div>
    </form>
  );
}
